import { Component, h, Listen, State, Element, Event, EventEmitter /*getAssetPath*/ } from '@stencil/core';
import { Cameras } from '../../../helpers/Cameras';

import store from '../../../helpers/store';
import { TranslationValues } from '../../../models/ITranslationValues';
import { Translations } from '../../../helpers/TranslationUtils';
import { FlowStatus } from '../../../models/FlowStatus';

import logoOntrace from '../../../assets/logo-ontrace.svg';
import { Browser, MobileOS } from '../../../models/IDevice';
import { addExifInImg } from '../../../helpers/security';
import { LoggerService } from '../../../helpers/LoggerService';

import '@tensorflow/tfjs-backend-webgl';
import '@tensorflow/tfjs-backend-cpu';
import { delay } from '../../../utils/utils';
import { modelLoadingService } from '../../../helpers/ModelLoadingService';

enum ImageFormat {
  JPEG = 'image/jpeg',
  PNG = 'image/png',
}

@Component({
  tag: 'selfie-auto-capture',
  styleUrl: 'selfie-auto-capture.css',
  shadow: false,
})
export class SelfieAutoCapture {
  private translations: TranslationValues;
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;

  @Element() component: HTMLElement;

  @State() recordedChunks: Blob[] = [];
  @State() private titleMesage: string;
  @State() private titleStyle: string;
  @State() capturedImage: Blob | null = null;

  @State() hasFace: boolean = false;
  @State() isDetecting: boolean = false;
  @State() elapsedTime: number = 0;
  @State() guideColor: string = 'face-guide-white';
  @State() countdown: number = 0;
  @State() isCapturing: boolean = false;
  @State() isLoading: boolean = true;

  private videoElement: HTMLVideoElement;
  private canvasElement: HTMLCanvasElement;
  // @ts-ignore
  private videoContainer: HTMLDivElement;
  private canvasCtx: CanvasRenderingContext2D | null = null;
  private animationFrameId: number | null = null;
  private timerInterval: NodeJS.Timeout | null = null;
  private readonly WARNING_THRESHOLD_SECONDS: number = 30;

  @Event({
    eventName: 'recordingSelfieCapture',
  })
  eventSelfieRecordingReady: EventEmitter;

  @Event({
    eventName: 'photoSelfieCapture',
  })
  eventPhotoCapture: EventEmitter;

  @Event({
    eventName: 'apiError',
  })
  apiErrorEvent: EventEmitter<any>;

  @Event({
    eventName: 'timeElapsed',
  })
  eventTimeElapsed: EventEmitter;

  constructor() {}

  @Listen('infoTimeElapsed')
  infoTimeElapsed() {
    this.titleStyle += ' pulsate';
  }

  async componentWillLoad() {
    this.translations = await Translations.getValues();
    if (!navigator.mediaDevices) {
      this.apiErrorEvent.emit({ message: 'This browser does not support webRTC' });
    }
    this.titleStyle = 'text-center px-1 mb-2';

    await this.loadModel();
  }

  async componentDidLoad() {
    if (store.flowStatus == FlowStatus.LIVENESS) {
      this.titleMesage = this.translations.SelfieCaptureValues.Waiting;
    }
    if (this.canvasElement) {
      this.canvasCtx = this.canvasElement.getContext('2d', { willReadFrequently: true });
    }

    if (!this.videoElement || !this.canvasElement || !this.canvasCtx) {
      LoggerService.error('Canvas or video element not found.');
      return;
    }

    await this.startWebcam();
    this.startRecording();
    await this.startDetection();

    this.timerInterval = setInterval(() => {
      this.elapsedTime++; // Increment elapsed time every second
      LoggerService.log(`Time open: ${this.elapsedTime} seconds`);

      if (this.elapsedTime >= this.WARNING_THRESHOLD_SECONDS) {
        if (this.timerInterval !== null) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
          LoggerService.warn(`Warning: Component has been open for ${this.WARNING_THRESHOLD_SECONDS} seconds!`);
        }
        this.eventTimeElapsed.emit();
      }
    }, 1000);
  }

  async startWebcam() {
    try {
      const constraints = Cameras.GetConstraints('', store.device, true, false);
      if (store.device.browser.name === Browser.Safari && (store.device.os.name === MobileOS.iOS || store.device.os.name === MobileOS.MacOS)) {
        //@ts-ignore
        navigator.audioSession.type = 'play-and-record';
      }
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      if (this.videoElement) {
        this.videoElement.srcObject = this.mediaStream;

        await new Promise<void>(resolve => {
          const onLoadedMetadata = () => {
            this.videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
            if (this.videoElement.videoWidth > 0 && this.videoElement.videoHeight > 0) {
              resolve();
            } else {
              onLoadedMetadata();
            }
          };
          this.videoElement.addEventListener('loadedmetadata', onLoadedMetadata);
        });

        await new Promise<void>(resolve => {
          const onLoadedData = () => {
            this.videoElement.removeEventListener('loadeddata', onLoadedData);
            resolve();
          };
          this.videoElement.addEventListener('loadeddata', onLoadedData);
        });

        this.videoElement.play();
        this.isLoading = false;
        this.titleMesage = this.translations.SelfieCaptureValues.Title;
      }
    } catch (error) {
      LoggerService.error('Error accessing webcam:', error);
      LoggerService.logMulti('Start webcam error:', error);
      alert('Could not access webcam. Please ensure you have a camera and grant permissions.');
      this.isLoading = true;
    }
  }

  startDetection = async () => {
    await delay(1000);
    if (this.videoElement && !this.isDetecting) {
      this.isDetecting = true;
      await this.detectFacesInRealTime();
    }
  };

  detectFacesInRealTime = async () => {
    if (!this.isDetecting || !this.videoElement || this.videoElement.paused || this.videoElement.ended) {
      return;
    }

    try {
      // Set canvas dimensions to match video
      this.canvasElement.width = this.videoElement.videoWidth;
      this.canvasElement.height = this.videoElement.videoHeight;

      this.canvasCtx.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
      this.canvasCtx.drawImage(this.videoElement, 0, 0, this.canvasElement.width, this.canvasElement.height);

      const faces = await modelLoadingService.detectFaces(this.videoElement);

      if (faces.length > 0) {
        this.hasFace = true;
        this.isDetecting = false;
        this.guideColor = 'face-guide-green';
        this.titleMesage = this.translations.SelfieCaptureValues.Waiting;
        clearInterval(this.timerInterval);
        if (this.animationFrameId !== null) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
        this.countdown = 3;
        const timer = setInterval(async () => {
          this.countdown--;
          if (this.countdown <= 0) {
            clearInterval(timer);
            await this.captureImage();
            this.stopRecording();
          }
        }, 1000);
      } else {
        this.hasFace = false;
      }
    } catch (error) {
      LoggerService.error('Error during face detection:', error);
      LoggerService.logMulti('Face detection error:', error);
    }
    if (this.isDetecting) {
      await delay(50);
      this.animationFrameId = requestAnimationFrame(async () => await this.detectFacesInRealTime());
    }
  };

  async loadModel() {
    await modelLoadingService.loadFaceDetectionModel();
  }

  disconnectedCallback() {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.canvasCtx) {
      this.canvasCtx.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
    }
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private startRecording = () => {
    if (!this.mediaStream || !this.videoElement || !store.videoRecording) {
      return;
    }
    this.recordedChunks = [];

    var options = { mimeType: Cameras.webmMimeType.mime, videoBitsPerSecond: 800000 };

    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      if (store.device.os.name === MobileOS.iOS || store.device.os.name === MobileOS.MacOS || store.device.browser.name === Browser.Safari)
        options.mimeType = Cameras.mp4MimeType.mime;
    }

    this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: 'video/webm' });

    this.mediaRecorder.ondataavailable = ({ data }) => {
      if (data.size > 0) {
        this.recordedChunks = [...this.recordedChunks, data];
      }
    };

    this.mediaRecorder.onstop = () => {
      if (this.recordedChunks.length > 0) {
        clearInterval(this.timerInterval);
        var rec = new Blob(this.recordedChunks, {
          type: options.mimeType.split(';')[0],
        });
        this.eventSelfieRecordingReady.emit(rec);
      }
    };

    this.mediaRecorder.start();
    LoggerService.log('Recording started...');
  };

  private stopRecording = () => {
    this.isLoading = true;
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
      LoggerService.log('Recording stopped.');
    } else {
      clearInterval(this.timerInterval);
      this.eventSelfieRecordingReady.emit(null);
    }
  };

  captureImage = async (): Promise<boolean> => {
    return new Promise(async resolve => {
      if (this.hasFace && this.canvasElement) {
        const video = this.videoElement;
        const canvas = this.canvasElement;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        this.canvasCtx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const predictions = await modelLoadingService.detectFaces(this.videoElement);

        if (predictions.length === 0) {
          this.eventTimeElapsed.emit();
          resolve(true);
        }

        canvas.toBlob(
          (frame: Blob) => {
            if (
              frame &&
              frame.type === ImageFormat.JPEG &&
              store.device.os.name !== MobileOS.iOS &&
              store.device.os.name !== MobileOS.MacOS &&
              store.device.browser.name !== Browser.Safari
            ) {
              try {
                LoggerService.log('stream | getFrame | addExifInImg');
                addExifInImg(frame, this.mediaStream.getTracks()[0], { width: this.videoElement.videoWidth, height: this.videoElement.videoHeight }).then(updatedFrame => {
                  this.handleCapture(updatedFrame, resolve);
                });
              } catch (e) {
                this.eventTimeElapsed.emit();
                resolve(false);
              }
            } else {
              LoggerService.log('stream | getFrame | resolve');
              this.handleCapture(frame, resolve);
            }
          },
          ImageFormat.PNG,
          1,
        );
        LoggerService.log('Image captured!');
        // You can now send this `dataUrl` to a server, display it, etc.
      } else {
        LoggerService.log('No face detected or canvas not ready. Cannot capture image.');
      }
    });
  };

  private handleCapture = (frame: Blob, resolve: (value: boolean | PromiseLike<boolean>) => void) => {
    this.isCapturing = true;
    setTimeout(() => {
      this.isCapturing = false;
      this.eventPhotoCapture.emit(frame);
      resolve(true);
    }, 400);
  };

  render() {
    return (
      <div class="container flex center">
        <div class="px-2 w-100">
          <h1 class={this.titleStyle} innerHTML={this.titleMesage}></h1>

          <div class="bg-black w-100 h-100 rounded" ref={el => (this.videoContainer = el)}>
            <div class="camera rounded">
              {this.isLoading && (
                <div class="loader-overlay">
                  <div class="loader-spinner"></div>
                </div>
              )}
              <video id="video" loop autoplay playsinline muted class="cameraVideoSelfie" ref={el => (this.videoElement = el)} />
              <canvas id="output" ref={el => (this.canvasElement = el)} style={{ display: 'none' }} />
              <div id="faceGuideline" class={this.guideColor}></div>
              {this.isCapturing && <div class="capture-flash-overlay"></div>}
              {this.countdown > 0 && (
                <div class="countdown-overlay-style">
                  <span class="countdown-text-style">{this.countdown}</span>
                </div>
              )}
            </div>
          </div>
          {store.debug == true && (
            <div class="text-center">
              <p>{store.requestId}</p>
            </div>
          )}
          <div class="footer text-center">
            <img src={logoOntrace}></img>
          </div>
        </div>
      </div>
    );
  }
}
